import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback } from 'react';
import * as yup from 'yup';
import { regexAcronymState, regexFiscalCode } from '../constants';
import EuropeanUnionList from './european-union-list';
import PmiTextField from 'shell/components/shared/text-field';

export interface ForeignStateForm {
  acronym?: string;
  code?: string;
  name?: string;
  exonym?: string;
  endonym?: string;
  citizenship?: string;
  citizenMale?: string;
  citizenFemale?: string;
  fiscalCode?: string;
  isoCode?: string;
  capitalCode?: string;
  capitalName?: string;
  releaseUE?: boolean;
  ue?: string;
  license?: string;
}

const validationSchema = yup.object().shape({
  acronym: yup
    .string()
    .required('Il campo sigla stato è obbligatorio')
    .min(1, 'Il campo sigla stato deve essere almeno di 1 caratteri')
    .max(3, 'Il campo sigla stato deve essere al massimo di 3 caratteri')
    .matches(regexAcronymState, 'Il campo sigla stato deve essere es: c oppure ci oppure ci*'),
  code: yup.string().required('Il campo codice stato è obbligatorio'),
  name: yup.string().required('Il campo nome breve è obbligatorio'),
  exonym: yup.string().required('Il campo esonimo è obbligatorio'),
  endonym: yup.string().required('Il campo endonimo è obbligatorio'),
  citizenship: yup.string().required('Il campo codice cittadinanza è obbligatorio'),
  citizenMale: yup.string().required('Il campo nome cittadino è obbligatorio'),
  citizenFemale: yup.string().required('Il campo nome cittadina è obbligatorio'),
  fiscalCode: yup
    .string()
    .required('Il campo codice fiscale è obbligatorio')
    .matches(regexFiscalCode, 'Il campo deve essere es: M123'),
  isoCode: yup.string().required('Il campo codice ISO-3166 è obbligatorio'),
  capitalCode: yup
    .number()
    .max(2147483647, 'Il campo supera il range massimo che è di 2147483647') // max value integer java
    .required('Il campo nome capitale è obbligatorio'),
  releaseUE: yup.boolean().required('Il campo Rilascio CI è obbligatorio'),
  ue: yup.string().required('Il campo Unione Europea è obbligatorio'),
  license: yup.string().required('Il campo codice è obbligatorio'),
});

interface FormForeignStateProps {
  onOk: (form: ForeignStateForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: ForeignStateForm;
  isEdit?: boolean;
}

const FormForeignState = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isEdit = false,
}: FormForeignStateProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formForeignState = useFormik<ForeignStateForm>({
    initialValues: initialValues || {
      code: '',
      name: '',
      acronym: '',
      exonym: '',
      endonym: '',
      citizenship: '',
      citizenMale: '',
      citizenFemale: '',
      fiscalCode: '',
      isoCode: '',
      capitalCode: '',
      capitalName: '',
      releaseUE: false,
      ue: '',
      license: '',
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formForeignState.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Sigla stato"
                  label="Sigla stato *"
                  id="acronym"
                  name="acronym"
                  inputProps={{
                    maxLength: '3',
                  }}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formForeignState.values.acronym || ''}
                  disabled={disabled || isEdit}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.acronym && Boolean(formForeignState.errors.acronym)
                  }
                  helperText={formForeignState.errors.acronym}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Codice stato"
                  label="Codice stato *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '3',
                  }}
                  variant="outlined"
                  value={formForeignState.values.code || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={formForeignState.touched.code && Boolean(formForeignState.errors.code)}
                  helperText={formForeignState.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Sigla Patente"
                  label="Sigla Patente *"
                  id="license"
                  name="license"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={formForeignState.values.license || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.license && Boolean(formForeignState.errors.license)
                  }
                  helperText={formForeignState.errors.license}
                  inputProps={{
                    maxLength: '3',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <EuropeanUnionList
                  value={formForeignState.values.ue || ''}
                  onChange={formForeignState.handleChange}
                  error={formForeignState.touched.ue && Boolean(formForeignState.errors.ue)}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Nome breve"
                  label="Nome breve *"
                  id="name"
                  name="name"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '20',
                  }}
                  variant="outlined"
                  value={formForeignState.values.name || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={formForeignState.touched.name && Boolean(formForeignState.errors.name)}
                  helperText={formForeignState.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Esonimo"
                  label="Esonimo *"
                  id="exonym"
                  name="exonym"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '100',
                  }}
                  value={formForeignState.values.exonym || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={formForeignState.touched.exonym && Boolean(formForeignState.errors.exonym)}
                  helperText={formForeignState.errors.exonym}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Endonimo"
                  label="Endonimo *"
                  id="endonym"
                  name="endonym"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '100',
                  }}
                  value={formForeignState.values.endonym || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.endonym && Boolean(formForeignState.errors.endonym)
                  }
                  helperText={formForeignState.errors.endonym}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Codice cittadinanza"
                  label="Cod. cittadinanza *"
                  id="citizenship"
                  name="citizenship"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    pattern: '[0-9]+',
                  }}
                  value={formForeignState.values.citizenship || ''}
                  disabled={disabled}
                  onChange={(event) =>
                    formForeignState.setFieldValue(
                      'citizenship',
                      event.target.validity.valid
                        ? event.target.value
                        : formForeignState.values.citizenship
                    )
                  }
                  error={
                    formForeignState.touched.citizenship &&
                    Boolean(formForeignState.errors.citizenship)
                  }
                  helperText={formForeignState.errors.citizenship}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="nome cittadino"
                  label="Nome cittadino *"
                  id="citizenMale"
                  name="citizenMale"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '50',
                  }}
                  variant="outlined"
                  value={formForeignState.values.citizenMale || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.citizenMale &&
                    Boolean(formForeignState.errors.citizenMale)
                  }
                  helperText={formForeignState.errors.citizenMale}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="nome cittadina"
                  label="Nome cittadina *"
                  id="citizenFemale"
                  name="citizenFemale"
                  fullWidth
                  inputProps={{
                    maxLength: '50',
                  }}
                  type="text"
                  variant="outlined"
                  value={formForeignState.values.citizenFemale || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.citizenFemale &&
                    Boolean(formForeignState.errors.citizenFemale)
                  }
                  helperText={formForeignState.errors.citizenFemale}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="codice fiscale"
                  label="Codice fiscale *"
                  id="fiscalCode"
                  name="fiscalCode"
                  inputProps={{
                    maxLength: '4',
                  }}
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={formForeignState.values.fiscalCode || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.fiscalCode &&
                    Boolean(formForeignState.errors.fiscalCode)
                  }
                  helperText={formForeignState.errors.fiscalCode}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice ISO-3166"
                  label="Codice ISO-3166 *"
                  id="isoCode"
                  name="isoCode"
                  inputProps={{
                    maxLength: '3',
                  }}
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={formForeignState.values.isoCode || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.isoCode && Boolean(formForeignState.errors.isoCode)
                  }
                  helperText={formForeignState.errors.isoCode}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice Capitale"
                  label="Codice Capitale *"
                  id="capitalCode"
                  name="capitalCode"
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={formForeignState.values.capitalCode || ''}
                  disabled={disabled}
                  onChange={formForeignState.handleChange}
                  error={
                    formForeignState.touched.capitalCode &&
                    Boolean(formForeignState.errors.capitalCode)
                  }
                  helperText={formForeignState.errors.capitalCode}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="releaseUE"
                      name="releaseUE"
                      checked={formForeignState.values.releaseUE || false}
                      onChange={() =>
                        formForeignState.setFieldValue(
                          'releaseUE',
                          !formForeignState.values.releaseUE
                        )
                      }
                      disabled={disabled}
                      inputProps={{ 'aria-label': 'Rilascio CI' }}
                    />
                  }
                  label="Rilascio CI"
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormForeignState.displayName = 'Form Foreign State component';

export default FormForeignState;
